import { NextPageContext } from 'next'
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  Observable,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client'
import { ErrorLink } from '@apollo/client/link/error'
import { HttpLink } from '@apollo/client/link/http'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { getMainDefinition } from '@apollo/client/utilities'
import { SetContextLink } from '@apollo/client/link/context'
import * as Sentry from '@sentry/nextjs'
// spanToTraceContext removed in favor of public span.spanContext() access
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { Defer20220824Handler } from '@apollo/client/incremental'
import { map } from 'rxjs/internal/operators/map'

import { configureBigIntSerialization } from '~/helpers/functions'

import possibleTypesResult from '~/__generated_types__/possibleTypes'

import type { NormalizedCacheObject } from '@apollo/client'

configureBigIntSerialization()

// AbortController.abort() surfaces as a DOMException in different shapes depending
// on where Apollo catches it (direct throw, networkError wrapper, or serialized message).
// We check all three so the errorLink can silently ignore routine query cancellations
// instead of logging them as real errors or triggering a redirect.
const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') return true

  const err = error as Record<string, unknown> | undefined

  return (
    (err?.networkError instanceof DOMException &&
      err.networkError.name === 'AbortError') ||
    (typeof err?.message === 'string' && err.message.includes('AbortError'))
  )
}

const errorLink = new ErrorLink(({ error, operation }) => {
  // Skip logging/redirect for aborted queries — these are intentional cancellations
  if (isAbortError(error)) return

  const errors: Array<string> = []

  if (
    ServerError.is(error) &&
    error.statusCode === 401 &&
    typeof window !== 'undefined'
  ) {
    window.location.assign(`/login?redirect=${window.location.pathname}`)
    errors.push(`[Network error]: ${error.toString()}, Code: ${error.statusCode}`)
  } else if (CombinedGraphQLErrors.is(error)) {
    errors.push(
      ...error.errors.map(
        ({ message, locations, path }) =>
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
            locations
          )}, Path: ${JSON.stringify(path)}`
      )
    )
  } else if (error) {
    errors.push(`[Network error]: ${error.toString()}`)
  }

  if (errors.length > 0) {
    const requestId = operation.getContext().headers?.['X-BATON-REQUEST-ID']
    const errorMessage = `[Request ${requestId ?? 'unknown'}]: ${errors.join(', ')}`

    if (process.env.NEXT_PUBLIC_ENV === 'development') {
      throw new Error(errorMessage)
    }
  }
})

// Workaround for crashes in ErrorLink: graphQLResultHasProtocolErrors crashes when
// result.extensions is null and Strawberry sends "extensions": null
// in multipart @defer chunks.
const sanitizeLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((result) => {
      if ('extensions' in result && result.extensions === null) {
        delete result.extensions
      }

      return result
    })
  )
})

const requestIdLink = new SetContextLink(() => {
  const requestId = `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`

  Sentry.setTag('request_id', requestId)

  return {
    headers: {
      'X-BATON-REQUEST-ID': requestId,
    },
  }
})

// Wraps fetch to gracefully handle AbortError when cancelling in-flight
// @defer multipart streams.
// See: https://github.com/apollographql/apollo-client/issues/13161
const abortSafeFetch: typeof fetch = async (input, init) => {
  return fetch(input, init).then((response) => {
    const contentType = response.headers.get('content-type') ?? ''
    if (!init?.signal || !response.body || !contentType.includes('multipart/mixed')) {
      return response
    }

    const reader = response.body.getReader()
    const stream = new ReadableStream({
      pull(controller) {
        return reader.read().then(
          ({ done, value }) => {
            if (done) {
              controller.close()
            } else {
              controller.enqueue(value)
            }
          },
          (err) => {
            if (err?.name === 'AbortError') {
              controller.close()
            } else {
              controller.error(err)
            }
          }
        )
      },
      cancel() {
        reader.cancel()
      },
    })

    return new Response(stream, response)
  })
}

const httpLinkOptions = {
  uri: process.env.NEXT_PUBLIC_SERVER_URL, // Server URL (must be absolute)
  credentials: 'include' as const,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'client-name': 'TMS Front End',
    'client-version': process.env.VERSION as string,
  },
  fetch: abortSafeFetch,
}

const httpLink = new HttpLink(httpLinkOptions)

const uploadHttpLink = new UploadHttpLink(httpLinkOptions)

const isUploadOperation = (operation: { variables: Record<string, unknown> }) => {
  return Object.values(operation.variables).some(
    (value) =>
      value instanceof File ||
      value instanceof Blob ||
      (Array.isArray(value) &&
        value.some((item) => item instanceof File || item instanceof Blob))
  )
}

const fileHttpLink = ApolloLink.split(
  ({ variables }) => isUploadOperation({ variables }),
  uploadHttpLink,
  httpLink
)

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${process.env.NEXT_PUBLIC_WS_SERVER_URL}`,
  })
)

export const sentryLink = new ApolloLink((operation, forward) => {
  return new Observable((outerObserver) => {
    let finished = false

    // Extract parent span from context if provided
    const context = operation.getContext()
    const parentSpan = context.parentSpan as Sentry.Span | undefined

    // Create span manually to control its lifecycle
    const span = Sentry.startInactiveSpan({
      name: operation.operationName || 'GraphQL',
      op: 'graphql.client',
      attributes: {
        ...operation.variables.query,
        variables: JSON.stringify(operation.variables.query),
      },
      ...(parentSpan && { parentSpan }),
    })

    const subscription = forward(operation).subscribe({
      next: (value) => outerObserver.next(value),
      error: (err) => {
        if (span) {
          span.setAttribute('graphql.error', true)
          if (!finished) {
            finished = true
            span.end()
          }
        }
        outerObserver.error(err)
      },
      complete: () => {
        if (span && !finished) {
          finished = true
          span.end()
        }
        outerObserver.complete()
      },
    })

    return () => {
      if (span && !finished) {
        finished = true
        span.end()
      }
      subscription.unsubscribe()
    }
  })
})

const createApolloClient = (
  initialState: NormalizedCacheObject,
  ctx: NextPageContext
): ApolloClient => {
  const links =
    typeof window !== 'undefined' && wsLink
      ? [
          requestIdLink,
          sentryLink,
          errorLink,
          sanitizeLink,
          ApolloLink.split(
            ({ query }) => {
              const definition = getMainDefinition(query)

              return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
              )
            },
            wsLink,
            fileHttpLink
          ),
        ]
      : [sentryLink, fileHttpLink]

  const cache = new InMemoryCache({
    possibleTypes: possibleTypesResult.possibleTypes,
  }).restore(initialState)

  return new ApolloClient({
    ssrMode: Boolean(ctx),
    link: ApolloLink.from(links),
    incrementalHandler: new Defer20220824Handler(),
    cache: cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  })
}

export default createApolloClient
